import { type SanityFrameBuilderModule } from '@frame-builder/data/sanity/queries/types'
import {
  type SanityBlock,
  type SanityContentCarouselBlock,
  type SanityCTABlock,
} from './blocks'
import {
  type SanityBlogAuthor,
  type SanityBlogCategory,
  type SanityBlogPostOptions,
  type SanityBlogPostWithoutBody,
} from './blog'
import { type SanityCartSettings, type SanityCollectionGridCart } from './cart'
import { type SanityColorValue } from './color'
import { type SanityComplexPortableText } from './content'
import { type SanityImageFragment } from './image'
import { type SanityLink } from './link'
import {
  type SanityCombinedListingProduct,
  type SanityProductFragment,
} from './product'
import { type SanitySlug } from './slug'
import { type SanityMuxVideo, type SanityVideo } from './video'

// Blog author post grid
export interface SanityBlogAuthorPostGridModule {
  _key: string
  _type: 'blogAuthorPostGrid'
  options?: SanityBlogPostOptions
  posts: SanityBlogPostWithoutBody[]
}

// Blog category post grid
export interface SanityBlogCategoryPostGridModule {
  _key: string
  _type: 'blogCategoryPostGrid'
  options?: SanityBlogPostOptions
  posts: SanityBlogPostWithoutBody[]
}

// Blog post body
export interface SanityBlogPostBodyModule {
  _key: string
  _type: 'blogPostBody'
  content: SanityComplexPortableText
}

// Blog post grid
export interface SanityBlogPostGridModule {
  _key: string
  _type: 'blogPostGrid'
  posts: SanityBlogPostWithoutBody[]
  author?: SanityBlogAuthor
  category?: SanityBlogCategory
  options?: SanityBlogPostOptions
}

// Blog post header
export interface SanityBlogPostHeaderModule {
  _key: string
  _type: 'blogPostHeader'
  post?: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

// Blog post item
export interface SanityBlogPostItemModule {
  _key: string
  _type: 'blogPostItem'
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
  background?: SanityColorValue
  spacing?: SanityResponsiveSpacing
}

// Blog post read more
export interface SanityBlogPostReadMoreModule {
  _key: string
  _type: 'blogPostReadMore'
  posts: SanityBlogPostWithoutBody[]
}

// Cart form
export interface SanityCartFormModule {
  _key: string
  _type: 'cartForm'
  active: boolean
  cartSettings?: SanityCartSettings
}

// Collection grid
interface SanityCollectionGridCollection {
  title?: string
  pageTitle?: string
  pageDescription?: string
  featuredProductIds?: number[]
  products?: SanityProductFragment[]
  productIds?: string[]
}

interface SanityCollectionGridCategory {
  title?: string
  pageTitle?: string
  pageDescription?: string
  products?: SanityProductFragment[]
}

interface SanityCollectionGridShop {
  description?: string
  title?: string
  featuredProductIds?: number[]
  products?: SanityProductFragment[]
}

export interface SanityCollectionStrings {
  collectionAllProducts: string
  collectionProducts: string
  collectionProductCount: string
  collectionSortLabel: string
  collectionSortDrawerTitle: string
  collectionSortDescription: string
  collectionShowFiltersLabel: string
  collectionFilters: string
  collectionRemoveFilter: string
  collectionClearAllLabel: string
  collectionClearFiltersLabel: string
  collectionFilterResultsLabel: string
  collectionFilterNoResultsLabel: string
  collectionFilterModalClose: string
  collectionFilterNoResults: SanityComplexPortableText
}

export interface SanityCollectionGridModule {
  _key: string
  _type: 'collectionGrid'
  active: boolean
  collection?: SanityCollectionGridCollection
  productCategory?: SanityCollectionGridCategory
  shop?: SanityCollectionGridShop
  cartSettings?: SanityCollectionGridCart
  collectionStrings: SanityCollectionStrings
}

// Content carousel
export type SanityContentCarouselModule = SanityContentCarouselBlock

// CTA
export type SanityCTAModule = SanityCTABlock

// Divider photo
export interface SanityDividerPhotoModule {
  _key: string
  _type: 'dividerPhoto'
  photo: SanityImageFragment
}

// Grid
type SanityGridColumnSizeWidth =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12

type SanityGridColumnSizeJustify =
  | 'justify-self-start'
  | 'justify-self-center'
  | 'justify-self-end'

type SanityGridColumnSizeAlign = 'self-start' | 'self-center' | 'self-end'

type SanityGridColumnSizeStart =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12

export interface SanityGridColumnSize {
  breakpoint: SanityBreakpoint
  width: SanityGridColumnSizeWidth
  justify?: SanityGridColumnSizeJustify
  align?: SanityGridColumnSizeAlign
  start?: SanityGridColumnSizeStart
  spacing?: SanitySpacingValues
}

export type SanityBreakpoint = ' ' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export type SanitySpacingValue =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 8
  | 10
  | 12
  | 16
  | 20
  | 24
  | 32
  | 40
  | 48
  | 56
  | 64

export interface SanitySpacingValues {
  top?: SanitySpacingValue
  right?: SanitySpacingValue
  bottom?: SanitySpacingValue
  left?: SanitySpacingValue
}

export interface SanityResponsiveSpacingBreakpoint {
  size: SanityBreakpoint
  spacing?: SanitySpacingValues
}

export type SanityResponsiveSpacing = SanityResponsiveSpacingBreakpoint[]

interface SanityGridColumn {
  _key: string
  sizes: SanityGridColumnSize[]
  blocks: SanityBlock[]
}

type SanityGridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

export interface SanityGridModule {
  _key: string
  _type: 'grid'
  columns: SanityGridColumn[]
  size: SanityGridSize
  background?: SanityColorValue
  noColumnGaps?: boolean
  noRowGaps?: boolean
  reverseSequence?: boolean
  spacing?: SanityResponsiveSpacing
}

type SanityHeroBackgroundType = 'photo' | 'lottie-animation' | 'mux-video'

interface SanityHeroPhotos {
  mobilePhoto?: SanityImageFragment
  desktopPhoto?: SanityImageFragment
}

export enum SanityHeroContentPosition {
  CENTER = 'center',
  BOTTOM_LEFT = 'bottom-left',
}

export interface SanityHeroModule {
  _key: string
  _type: 'hero'
  contentPosition: SanityHeroContentPosition
  content?: SanityComplexPortableText
  bgType: SanityHeroBackgroundType
  photos?: SanityHeroPhotos
  lottieAssetUrl?: string
  muxVideo?: SanityMuxVideo
}

// Image and text
export interface SanityImageAndTextModule {
  _key: string
  _type: 'imageAndText'
  image: SanityImageFragment
  textContent: SanityComplexPortableText
  textPosition: 'left' | 'right'
  background?: SanityColorValue
  spacing?: SanityResponsiveSpacing
}

// Logo grid
export interface SanityLogoGridModule {
  _key: string
  _type: 'logoGrid'
  title?: string
  logos: SanityImageFragment[]
}

// Marquee
interface SanityMarqueeSimpleItem {
  _key: string
  _type: 'simple'
  text: string
}

type SanityMarqueePhotoItem = Omit<SanityImageFragment, '_type'> & {
  _key: string
  _type: 'photo'
}

interface SanityMarqueeProductItem {
  _key: string
  _type: 'product'
  _id: string
  product: SanityProductFragment
}

type SanityMarqueeItem =
  | SanityMarqueeSimpleItem
  | SanityMarqueePhotoItem
  | SanityMarqueeProductItem

type SanityMarqueeContentAlignment = 'left' | 'center' | 'right'

export interface SanityMarqueeModule {
  _key: string
  _type: 'marquee'
  items: SanityMarqueeItem[]
  content?: SanityComplexPortableText
  contentAlignment?: SanityMarqueeContentAlignment
  speed?: number
  reverse?: boolean
  pausable?: boolean
}

// Pricing plans
type SanityPricingPlanButton = SanityLink & {
  label?: string
}

interface SanityPricingPlan {
  name?: string
  price?: string
  description?: string
  featured?: boolean
  features?: string
  button?: SanityPricingPlanButton
}

export interface SanityPricingPlansModule {
  _key: string
  _type: 'pricingPlans'
  plans: SanityPricingPlan[]
}

// Product hero
export interface SanityProductHeroModule {
  _key: string
  _type: 'productHero'
  active: boolean
  product?: SanityCombinedListingProduct
}

// Team members grid
interface SanityTeamMember {
  name: string
  slug: SanitySlug
  jobTitle?: string
  about?: string
  linkedInUrl?: string
  blogAuthor?: {
    slug: SanitySlug
  }
  photo?: SanityImageFragment
}

export interface SanityTeamMembersGridModule {
  _key: string
  _type: 'teamMembersGrid'
  title?: string
  subtitle?: string
  teamMembers?: SanityTeamMember[]
}

// Video
export type SanityVideoModule = SanityVideo & {
  _key: string
  _type: 'video'
}

type SanityBaseModule =
  | SanityBlogAuthorPostGridModule
  | SanityBlogCategoryPostGridModule
  | SanityBlogPostBodyModule
  | SanityBlogPostGridModule
  | SanityBlogPostHeaderModule
  | SanityBlogPostItemModule
  | SanityBlogPostReadMoreModule
  | SanityCartFormModule
  | SanityCollectionGridModule
  | SanityContentCarouselModule
  | SanityCTAModule
  | SanityDividerPhotoModule
  | SanityFrameBuilderModule
  | SanityGridModule
  | SanityHeroModule
  | SanityImageAndTextModule
  | SanityLogoGridModule
  | SanityMarqueeModule
  | SanityPricingPlansModule
  | SanityProductHeroModule
  | SanityTeamMembersGridModule
  | SanityVideoModule

interface SanityPredefinedPageModule {
  pageModules: SanityBaseModule[]
}

export type SanityPredefinedPageModuleModule = SanityPredefinedPageModule & {
  _key: string
  _type: 'predefinedPageModule'
}

export type SanityModule = SanityBaseModule | SanityPredefinedPageModuleModule
